import React from 'react';
import ButtonClose from 'shared/ButtonClose/ButtonClose';
import Logo from 'shared/Logo/Logo';
import { Disclosure } from '@headlessui/react';
import { NavLink } from 'react-router-dom';
import { NavItemType } from './NavigationItem';
import { NAVIGATION_DEMO } from 'data/navigation';
import SocialsList from 'shared/SocialsList/SocialsList';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import SwitchDarkMode from 'shared/SwitchDarkMode/SwitchDarkMode';
import { useUserSessionStore } from '../../stores/UserSession.store';
import { useAuth } from '../../hooks/Auth/useAuth';
import { UserTypeEnum } from '../../@core/enums/UserType.enum';
import ncNanoId from '../../utils/ncNanoId';
import { useServices } from '../../hooks/collections/useServices';

export interface NavMobileProps {
  data?: NavItemType[];
  onClickClose?: () => void;
}

const NavMobile: React.FC<NavMobileProps> = ({
  data = NAVIGATION_DEMO,
  onClickClose,
}) => {
  const user = useUserSessionStore((state) => state.user);
  const { handleSignOut } = useAuth();
  const { services } = useServices(true, [
    {
      field: 'featured',
      type: 'where',
      operator: '==',
      value: true,
    },
    {
      type: 'limit',
      value: 5,
    }
  ])

  const _renderMenuChild = (item: NavItemType) => {
    return (
      <ul className="nav-mobile-sub-menu pl-6 pb-1 text-base">
        {item.children?.map((i, index) => (
          <Disclosure key={i.href + index} as="li">
            <NavLink
              end
              to={{
                pathname: i.href || undefined,
              }}
              className={({ isActive }) =>
                `flex px-4 text-neutral-900 dark:text-neutral-200 text-sm font-medium rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 mt-0.5 ${
                  isActive ? 'text-secondary' : ''
                }`
              }
            >
              <span
                className={`py-2.5 pr-3 ${!i.children ? 'block w-full' : ''}`}
              >
                {i.name}
              </span>
              {i.children && (
                <span
                  className="flex-1 flex"
                  onClick={(e) => e.preventDefault()}
                >
                  <Disclosure.Button
                    as="span"
                    className="py-2.5 flex justify-end flex-1"
                  >
                    <ChevronDownIcon
                      className="ml-2 h-4 w-4 text-neutral-500"
                      aria-hidden="true"
                    />
                  </Disclosure.Button>
                </span>
              )}
            </NavLink>
            {i.children && (
              <Disclosure.Panel>{_renderMenuChild(i)}</Disclosure.Panel>
            )}
          </Disclosure>
        ))}
      </ul>
    );
  };

  const _renderItem = (item: NavItemType) => {
    return (
      <Disclosure
        key={item.id}
        as="li"
        className="text-neutral-900 dark:text-white"
      >
        <NavLink
          end
          className={({ isActive }) =>
            `flex w-full px-4 font-medium uppercase tracking-wide text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg ${
              isActive ? 'text-secondary' : ''
            }`
          }
          to={{
            pathname: item.href || undefined,
          }}
        >
          <span
            className={`py-2.5 pr-3 ${!item.children ? 'block w-full' : ''}`}
          >
            {item.name}
          </span>
          {item.children && (
            <span className="flex-1 flex" onClick={(e) => e.preventDefault()}>
              <Disclosure.Button
                as="span"
                className="py-2.5 flex items-center justify-end flex-1 "
              >
                <ChevronDownIcon
                  className="ml-2 h-4 w-4 text-neutral-500"
                  aria-hidden="true"
                />
              </Disclosure.Button>
            </span>
          )}
        </NavLink>
        {item.children && (
          <Disclosure.Panel>{_renderMenuChild(item)}</Disclosure.Panel>
        )}
      </Disclosure>
    );
  };

  return (
    <div className="overflow-y-auto w-full h-screen py-2 transition transform shadow-lg ring-1 dark:ring-neutral-700 bg-white dark:bg-neutral-900 divide-y-2 divide-neutral-100 dark:divide-neutral-800">
      <div className="py-6 px-5">
        <Logo/>
        <div className="flex flex-col mt-5 text-neutral-700 dark:text-neutral-300 text-sm">
          <span>
            Whether you're shopping, at work, or just out for a coffee, your car gets a professional wash—without disrupting your day.
          </span>

          <div className="flex justify-between items-center mt-4">
            <SocialsList itemClass="w-9 h-9 flex items-center justify-center rounded-full bg-neutral-100 text-xl dark:bg-neutral-800 dark:text-neutral-300"/>
            <span className="block">
              <SwitchDarkMode className="bg-neutral-100 dark:bg-neutral-800"/>
            </span>
          </div>
        </div>
        <span className="absolute right-2 top-2 p-1">
          <ButtonClose onClick={onClickClose}/>
        </span>
      </div>
      <ul className="flex flex-col py-6 px-2 space-y-1">
        {user ? (_renderItem({
          id: 'account',
          href: '/account',
          name: 'Account',
        })) : (
          <>
            {_renderItem({
              id: ncNanoId(),
              href: '/signup',
              name: 'Sign Up',
            })}
            {_renderItem({
              id: ncNanoId(),
              href: '/login',
              name: 'Sign In',
            })}
          </>
        )}
        {_renderItem({
          id: ncNanoId(),
          href: services.length ? undefined : '/services',
          name: 'Services',
          children: services.length
            ? services.map(service => ({
              id: service.id,
              href: `/service-detail/${service.id}`,
              name: service.title,
            }))
            : undefined,
        })}
        {data.map(_renderItem)}
        {user && (
          <li>
            <div
              onClick={handleSignOut}
              className={'px-4 py-2.5 font-medium uppercase tracking-wide text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg hover:cursor-pointer'}
            >
              SIGN OUT
            </div>
          </li>
        )}
        {user?.role === UserTypeEnum.COMPANY && (
          _renderItem({
            id: ncNanoId(),
            href: '/company',
            name: 'Business',
            children: [
              {
                id: ncNanoId(),
                href: '/account-services',
                name: 'Services',
              },
              {
                id: ncNanoId(),
                href: '/account-company-chats',
                name: 'Platform Interactions',
              },
              {
                id: ncNanoId(),
                href: '/account-terms-and-conditions',
                name: 'Website Management',
              },
            ],
          })
        )}
      </ul>
    </div>
  );
};

export default NavMobile;
